import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { NewLead } from 'app/models/dto/add-new-lead';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { StatesService } from 'app/services/states/states.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-add-more-info-lead-form',
  templateUrl: './add-more-info-lead-form.component.html',
  styleUrls: ['./add-more-info-lead-form.component.scss', '../../scss/material/_dialog.scss']
})

export class AddMoreInfoLeadFormComponent implements OnInit {

  addBusinessForm = new UntypedFormGroup({});
  formOptions: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [];

  @Input() loading$: Observable<boolean>;
  @Input() duplicateLead$: Observable<boolean>;
  @Input() showErrorMessage$: Observable<boolean>;
  @Input() errMessage: string;

  @Output() saveData = new EventEmitter<any>();
  @Output() newLeadErrorMessageDismissed = new EventEmitter<void>();

  model: Partial<NewLead> = {
    monthlyCharges: undefined,
    bankruptcy: false,
    loanPurpose: '',
    entity_type: '',
    street: '',
    city: '',
    state: '',
    zipId: undefined,
    pastFunds: ''
  };

  constructor(
    private statesService: StatesService,
    private localeSettingService: LocaleSettingService
  ) { }

  ngOnInit(): void {
    this.fields = [
      {
        key: 'monthlyCharges',
        type: 'currency',
        templateOptions: {
          label: 'Monthly Credit Card Sales',
          required: false,
        },
        expressionProperties: {
          'templateOptions.maskOptions': model => ({
              mask: 'separator',
              prefix: model.monthlyCharges && this.localeSettingService.getLocaleCurrencySymbol()
            }),
        }
      },

      {
        key: 'bankruptcy',
        type: 'radio',
        className: 'bankruptcy-options',
        templateOptions: {
          label: 'Have you ever had a bankruptcy?',
          required: false,
          options: [
            { value: 'Y', label: 'Yes' },
            { value: 'N', label: 'No' }
          ]
        }
      },

      {
        key: 'loanPurpose',
        type: 'select',
        templateOptions: {
          label: 'Loan Purpose',
          required: false,
          multiple: true,
          appearance: 'outline',
          options: [
            { value: 'expansion', label: 'Expansion' },
            { value: 'workingCapital', label: 'Working Capital' },
            { value: 'payroll', label: 'Payroll' },
            { value: 'purchaseBusiness', label: 'Purchase a business' },
            { value: 'purchaseFranchise', label: 'Purchase a franchise' },
            { value: 'equipment', label: 'Equipment' },
            { value: 'realEstate', label: 'Real Estate' },
            { value: 'buyingPartner', label: 'Buying out a partner' },
            { value: 'startBusiness', label: 'Start a business' },
            { value: 'financeAccountsReceivable', label: 'Finance accounts receivable' },
            { value: 'other', label: 'Other' },
          ]
        }
      },

      {
        key: 'entity_type',
        type: 'select',
        templateOptions: {
          label: 'Entity Type',
          required: false,
          appearance: 'outline',
          options: [
            { value: 'llc', label: 'LLC' },
            { value: 'corporation', label: 'Corporation' },
            { value: 'soleProprietor', label: 'Sole Proprietor' },
            { value: 'legalPartnership', label: 'Legal Partnership' },
          ],
          attributes: {
            'data-cy': 'entity-type'
          },
        }
      },

      {
        key: 'street',
        type: 'input',
        templateOptions: {
          label: 'Owner Street Address',
          placeholder: 'Enter Location',
          required: false,
        }
      },
      {
        key: 'street2',
        type: 'input',
        templateOptions: {
          label: 'Owner Street 2',
          placeholder: 'Enter Location',
          required: false,
        }
      },
      {
        key: 'street3',
        type: 'input',
        templateOptions: {
          label: 'Owner Street 3',
          placeholder: 'Enter Location',
          required: false,
        },
        hideExpression: this.localeSettingService.getLocale() === 'en-US'
      },
      {
        key: 'city',
        type: 'input',
        templateOptions: {
          label: 'Owner City',
          placeholder: 'City',
          required: false,
          attributes: {
            'data-cy': 'ownerCity'
          },
        }
      },

      {
        fieldGroupClassName: 'owner_state',
        fieldGroup: [
          {
            key: 'state',
            className: 'state',
            type: 'select',
            templateOptions: {
              label: 'Owner State',
              required: false,
              appearance: 'outline',
              options: [
                ...this.getStatesList().map((state: { label: string, value: string }) => ({
                  label: state.label,
                  value: state.value
                }))
              ],
              attributes: {
                'data-cy': 'ownerState',
              },
            },
            hideExpression: this.localeSettingService.getLocale() === 'en-GB'
          },
          {
            key: 'zipId',
            type: 'input',
            className: 'ownerZipCode',
            templateOptions: {
              label: 'Owner Zip Code',
              placeholder: 'Zip Code',
              maxLength: 8,
              required: false,
              attributes: {
                'data-cy': 'ownerZipCode',
              },
            },
            expressionProperties: {
              'templateOptions.label': (model: any) => 'Owner ' + this.localeSettingService.getLocalePostalCodeName(model.country),
              'templateOptions.placeholder': (model: any) => this.localeSettingService.getLocalePostalCodeName(model.country)
            },
            validation: {
              show: true,
            },
            validators: {
              zipId: {
                expression: (c) => {
                  if (c.value) {
                    return c && c._parent && c._parent.value && c._parent.value.country
                      ? this.localeSettingService.getLocalePostalCodeRegex(c._parent.value.country).test(c.value)
                      : this.localeSettingService.getLocalePostalCodeRegex().test(c.value);
                  }
                  return true;
                },
                message: () => 'Invalid format',
              },
            },
          },
        ]
      },

      {
        key: 'pastFunds',
        type: 'textarea',
        templateOptions: {
          required: false,
          rows: 4,
          placeholder: 'Have you applied for funds for your business in the past, if yes, what happened?',
        }
      },
    ];
  }

  submit() {

    const obj = {} as NewLead;

    Object.keys(this.model).forEach((key) => {
      if (this.model[key]) {
        obj[key] = this.model[key];
      }
    });

    if (this.model.pastFunds) {
      obj.notes = {
        pastFunds: this.model.pastFunds
      };
    }
    this.saveData.emit(obj);
  }

  getStatesList() {
    return this.statesService.getAllStates();
  }

}
